import { Card } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  INVOICE_TRANSFORMATION_LIMITS,
  InvoiceTransformationAvailableField,
  InvoiceTransformationOutputModeEnum,
  InvoiceTransformationRequestUi,
  InvoiceTransformationRequestUiSchema,
  InvoiceTransformationResponse,
} from '@monetize/types/app';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { handleApiErrorToast } from '../../api/axios';
import { useUpsertInvoiceTransformation } from '../../api/invoiceTransformationService';
import { logger } from '../../services/logger';
import {
  MAlert,
  MBox,
  MButton,
  MCard,
  MCustomRadioGroup,
  MFlex,
  MFormField,
  MText,
} from '../Monetize';
import { InvoiceTransformationAddButton } from './InvoiceTransformationAddButton';
import { InvoiceTransformationLineRuleItem } from './InvoiceTransformationLineRuleItem';
import {
  getArrayErrorMessage,
  getNewLineRule,
  OUTPUT_MODE_OPTIONS,
  toApiPayload,
  toUiForm,
} from './invoiceTransformation.utils';

export const INVOICE_TRANSFORMATION_FORM_ID = 'invoice-transformation-form';

interface InvoiceTransformationFormProps {
  billGroupId: string;
  accountId: string;
  initialData: InvoiceTransformationResponse | null | undefined;
  availableFields: InvoiceTransformationAvailableField[];
  isEditing: boolean;
  onSave: () => void;
  setIsSubmitting: (isSubmitting: boolean) => void;
  onDirtyChange?: (isDirty: boolean) => void;
}

export const InvoiceTransformationForm: FC<InvoiceTransformationFormProps> = ({
  billGroupId,
  accountId,
  initialData,
  availableFields,
  isEditing,
  onSave,
  setIsSubmitting,
  onDirtyChange,
}) => {
  const defaultValues = useMemo(
    () => toUiForm(billGroupId, initialData),
    [billGroupId, initialData],
  );

  const formContext = useForm<InvoiceTransformationRequestUi>({
    resolver: zodResolver(InvoiceTransformationRequestUiSchema),
    mode: 'onChange',
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    getValues,
    watch,
    formState: { isDirty, errors },
  } = formContext;

  const mode = watch('mode');
  const isReadOnly = !isEditing;

  // Only report dirtiness while editing — in read-only mode the form is
  // disabled and should never make the drawer think it has unsaved changes.
  useEffect(() => {
    onDirtyChange?.(isEditing && isDirty);
  }, [isEditing, isDirty, onDirtyChange]);

  const { fields, append, remove, move, insert } = useFieldArray({
    name: 'invoiceLineRules',
    control,
  });

  // Lock the output mode once any rule exists — the available fields differ by
  // mode, so switching afterward could strand already-entered rule data.
  const isModeLocked = fields.length > 0;
  const isAtRuleLimit =
    fields.length >= INVOICE_TRANSFORMATION_LIMITS.invoiceLineRules.max;

  const { mutateAsync: upsert } = useUpsertInvoiceTransformation();

  const handleMoveUp = useCallback(
    (index: number) => {
      if (index > 0) {
        move(index, index - 1);
      }
    },
    [move],
  );

  const handleMoveDown = useCallback(
    (index: number) => {
      if (index < fields.length - 1) {
        move(index, index + 1);
      }
    },
    [move, fields.length],
  );

  const handleDuplicate = (index: number) => {
    const current = getValues(`invoiceLineRules.${index}`);
    if (!current || isAtRuleLimit) {
      return;
    }
    const cloned = structuredClone(current);
    cloned.name = cloned.name ? `${cloned.name} (Copy)` : '';
    insert(index + 1, cloned);
  };

  const onSubmit = async (data: InvoiceTransformationRequestUi) => {
    setIsSubmitting(true);
    try {
      const payload = toApiPayload(data);
      await upsert(payload);
      onSave();
    } catch (ex) {
      logger.warn(ex);
      handleApiErrorToast(ex);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onError = (err: unknown) => {
    logger.log('invoice transformation form validation error', err);
  };

  const isLineTransformation =
    mode === InvoiceTransformationOutputModeEnum.LINE_TRANSFORMATION;
  const splitTargetSingular = isLineTransformation ? 'line' : 'invoice';

  return (
    <form
      id={INVOICE_TRANSFORMATION_FORM_ID}
      onSubmit={handleSubmit(onSubmit, onError)}
      noValidate
    >
      <MBox>
        <MAlert type="info" dismissable mb={4}>
          <MBox fontSize="sm">
            <MText>
              Rules run <strong>top to bottom</strong>. Each invoice line is
              assigned to the first rule it matches and ignored by all later
              rules.
            </MText>
            <MText mt={2}>
              <strong>Conditions</strong> filter which lines a rule targets;
              leave them empty to catch any lines not matched by earlier rules.
            </MText>
            <MText mt={2}>
              Each rule produces one {splitTargetSingular} by default. Add a{' '}
              <strong>Group By</strong> to split matched lines further,
              generating one {splitTargetSingular} per distinct value.
            </MText>
            <MText mt={2} color="tGray.darkPurple">
              You can add up to{' '}
              {INVOICE_TRANSFORMATION_LIMITS.invoiceLineRules.max} rules. Each
              rule can have up to{' '}
              {INVOICE_TRANSFORMATION_LIMITS.conditionGroups.max} condition
              sets, and each condition set can have up to{' '}
              {INVOICE_TRANSFORMATION_LIMITS.conditions.max} conditions and{' '}
              {INVOICE_TRANSFORMATION_LIMITS.groupBy.max} Group By fields.
            </MText>
          </MBox>
        </MAlert>

        <MCard variant="borderless" mb={4}>
          <Card.Body>
            <MFormField
              label="Output mode"
              isRequired
              // Once rules exist, lock the mode — switching modes changes which
              // fields apply (e.g. bill group overrides), which could leave
              // already-entered rule data in a bad state. Only explain the lock
              // while editing; in read-only mode everything is disabled anyway.
              tooltip={
                isEditing && isModeLocked
                  ? 'Delete all rules to change the output mode.'
                  : undefined
              }
            >
              <Controller
                name="mode"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <MCustomRadioGroup
                    items={OUTPUT_MODE_OPTIONS}
                    itemTitle="title"
                    itemValue="value"
                    itemDescription="description"
                    value={value}
                    onChange={onChange}
                    disabled={isModeLocked || isReadOnly}
                  />
                )}
              />
            </MFormField>
          </Card.Body>
        </MCard>

        {fields.length === 0 ? (
          <MCard variant="borderless">
            <Card.Body>
              {isReadOnly ? (
                <MText fontSize="sm" color="tGray.darkPurple">
                  No rules configured yet.
                </MText>
              ) : (
                <MButton
                  variant="primary"
                  size="sm"
                  onClick={() => append(getNewLineRule(mode))}
                >
                  + Add Rule
                </MButton>
              )}
            </Card.Body>
          </MCard>
        ) : (
          <>
            {fields.map((fieldItem, index) => (
              <InvoiceTransformationLineRuleItem
                key={fieldItem.id}
                lineRuleIndex={index}
                accountId={accountId}
                availableFields={availableFields}
                formContext={formContext}
                isReadOnly={isReadOnly}
                canMoveUp={index > 0}
                canMoveDown={index < fields.length - 1}
                onMoveUp={() => handleMoveUp(index)}
                onMoveDown={() => handleMoveDown(index)}
                onRemove={() => remove(index)}
                isDuplicateDisabled={isAtRuleLimit}
                onDuplicate={() => handleDuplicate(index)}
              />
            ))}
            {!isReadOnly && (
              <MFlex mt={2} align="center" gap={3}>
                <InvoiceTransformationAddButton
                  label="+ Add Rule"
                  count={fields.length}
                  limit={INVOICE_TRANSFORMATION_LIMITS.invoiceLineRules}
                  error={getArrayErrorMessage(errors.invoiceLineRules)}
                  onClick={() => append(getNewLineRule(mode))}
                />
              </MFlex>
            )}
          </>
        )}
      </MBox>
    </form>
  );
};
